import { Box, Button, Icon, OsStack, Typography } from "@outschool/backpack";
import { faEdit } from "@outschool/icons";
import { isNullOrEmpty } from "@outschool/text";
import { gql, useMutation } from "@outschool/ui-apollo";
import { useShouldShowKakao, useShouldShowLine } from "@outschool/ui-auth";
import { Card, HeroLayout } from "@outschool/ui-components-classroom";
import {
  LearnerConsentChip,
  LearnerHealthConsentModal,
  LearnerMarketingConsentModal,
  useFeatureFlag,
} from "@outschool/ui-components-shared";
import {
  FormField,
  Input,
  Loading,
} from "@outschool/ui-legacy-component-library";
import React, { useState } from "react";

import { hasAccess } from "../../../../shared/Learner";
import BackButton from "../../components/BackButton";
// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import { LearnerAccessLevel } from "../../generated/graphql";
import Footer from "../../layout/nav/Footer";
import Header from "../../layout/nav/Header";
import useAnalyticsPage, {
  PageType,
} from "../../lib/analytics/useAnalyticsPage";
import {
  getCurrentLearner,
  useCurrentLearner,
} from "../../providers/CurrentLearnerProvider";
import ChangePasswordSection from "./ChangePassword";
import ConnectApple from "./ConnectThirdPartyAccounts/ConnectApple";
import ConnectFacebook from "./ConnectThirdPartyAccounts/ConnectFacebook";
import ConnectGoogle from "./ConnectThirdPartyAccounts/ConnectGoogle";
import ConnectKakao from "./ConnectThirdPartyAccounts/ConnectKakao";
import ConnectLine from "./ConnectThirdPartyAccounts/ConnectLine";

import type { SxProps } from "@outschool/backpack";

const SET_CURRENT_LEARNER_CONSENT_MUTATION = gql`
  mutation SetCurrentLearnerConsent(
    $hasMarketingConsent: Boolean
    $hasHealthConsent: Boolean
  ) {
    setCurrentLearnerConsent(
      hasMarketingConsent: $hasMarketingConsent
      hasHealthConsent: $hasHealthConsent
    ) {
      uid
    }
  }
`;

function LearnerConsentRow({
  title,
  description,
  consentStatus,
  sx,
  onClickChange,
}: {
  title: string;
  description: string;
  consentStatus: boolean | null;
  sx?: SxProps;
  onClickChange?: () => void;
}) {
  return (
    <Box sx={sx}>
      <OsStack direction="column" spacing={4}>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: "grey.700" }}>
          {description}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginTop: 4,
          }}
        >
          <LearnerConsentChip consentStatus={consentStatus} />
          <Button
            variant="link"
            onClick={onClickChange}
            sx={{
              fontSize: "1.4rem",
              fontWeight: 500,
              color: "primary.700",
              "&:hover": {
                color: "primary.600",
              },
            }}
          >
            <Icon icon={faEdit} sx={{ marginRight: 4 }} />
            Change
          </Button>
        </Box>
      </OsStack>
    </Box>
  );
}

const Settings = () => {
  useAnalyticsPage({ pageName: PageType.Settings });
  const showKakao = useShouldShowKakao();
  const showLine = useShouldShowLine();
  const currentLearner = useCurrentLearner();

  const enableLearnerConsent = useFeatureFlag("aic-learner-consent-settings");

  const [isMarketingConsentModalOpen, setIsMarketingConsentModalOpen] =
    useState(false);
  const [isHealthConsentModalOpen, setIsHealthConsentModalOpen] =
    useState(false);

  const [setConsent] = useMutation(SET_CURRENT_LEARNER_CONSENT_MUTATION, {
    refetchQueries: [{ query: getCurrentLearner }],
  });

  if (!currentLearner) {
    return <Loading />;
  }

  const isLearner13 = currentLearner.age && currentLearner.age >= 13;

  return (
    <HeroLayout
      header={<Header includeBlob />}
      footer={<Footer />}
      includeBackgroundGradient
    >
      <BackButton />
      <Card
        title="Account Settings"
        sx={{ maxWidth: "800px", margin: "0 auto 40px" }}
      >
        {!currentLearner.isLoginEnabled ? (
          <>
            <Typography
              variant="h3"
              sx={{
                marginTop: "1em",
                marginBottom: "0.5em",
              }}
            >
              Login Information
            </Typography>
            <Typography variant="inherit">
              Logging in directly is not enabled for you.
            </Typography>
          </>
        ) : (
          <>
            <Typography
              variant="h3"
              sx={{
                marginTop: "1em",
                marginBottom: "0.5em",
              }}
            >
              Login Information
            </Typography>
            <FormField
              label={
                isNullOrEmpty(currentLearner.username) ? "Email" : "Username"
              }
              sx={{ maxWidth: "40ch" }}
              labelSx={{ marginBottom: 0 }}
            >
              <Input
                value={
                  (isNullOrEmpty(currentLearner.username)
                    ? currentLearner.email
                    : currentLearner.username) ?? ""
                }
                disabled
              />
            </FormField>
            <ChangePasswordSection />

            {/* eslint-disable-next-line jsx-no-leaked-values/jsx-no-leaked-values */}
            {isLearner13 && enableLearnerConsent && (
              <>
                <Typography
                  variant="h3"
                  sx={{
                    marginTop: "1em",
                    marginBottom: "0.5em",
                  }}
                >
                  Privacy and personalization
                </Typography>
                <OsStack direction="column" spacing={16}>
                  <LearnerConsentRow
                    title="Activity based recommendations"
                    description="This information can improve class recommendations. You choose whether we can use it for recommendations. Uses saved classes, favorites, and searches to recommend classes."
                    consentStatus={currentLearner.hasMarketingConsent ?? null}
                    onClickChange={() => setIsMarketingConsentModalOpen(true)}
                  />
                  <LearnerConsentRow
                    title="Learning needs recommendations"
                    description="Uses learning needs or other health information to tailor class recommendations."
                    consentStatus={currentLearner.hasHealthConsent ?? null}
                    onClickChange={() => setIsHealthConsentModalOpen(true)}
                  />
                </OsStack>

                <LearnerMarketingConsentModal
                  open={isMarketingConsentModalOpen}
                  learnerName={currentLearner.name ?? ""}
                  onSave={async hasAgreed => {
                    await setConsent({
                      variables: { hasMarketingConsent: hasAgreed },
                    });
                  }}
                  onClose={() => setIsMarketingConsentModalOpen(false)}
                />

                <LearnerHealthConsentModal
                  open={isHealthConsentModalOpen}
                  learnerName={currentLearner.name ?? ""}
                  onSave={async hasAgreed => {
                    await setConsent({
                      variables: { hasHealthConsent: hasAgreed },
                    });
                  }}
                  onClose={() => setIsHealthConsentModalOpen(false)}
                />
              </>
            )}

            {hasAccess({
              currentLearner,
              minimumAccessLevel: LearnerAccessLevel.Limited,
            }) && (
              <>
                <Typography
                  variant="h3"
                  sx={{
                    marginTop: "1em",
                    marginBottom: "0.5em",
                  }}
                >
                  Connect with your third-party accounts to log in
                </Typography>
                {/* eslint-disable-next-line jsx-no-leaked-values/jsx-no-leaked-values */}
                {isLearner13 && <ConnectGoogle />}
                <ConnectApple />
                {/* eslint-disable-next-line jsx-no-leaked-values/jsx-no-leaked-values */}
                {isLearner13 && <ConnectFacebook />}
                {/* eslint-disable-next-line jsx-no-leaked-values/jsx-no-leaked-values */}
                {isLearner13 && showLine && <ConnectLine />}
                {/* eslint-disable-next-line jsx-no-leaked-values/jsx-no-leaked-values */}
                {isLearner13 && showKakao && <ConnectKakao />}
              </>
            )}
          </>
        )}
      </Card>
    </HeroLayout>
  );
};

export default Settings;
